'use client';

import type { ReactNode } from 'react';
import type { MediaCard } from 'src/sections/search-all/utils/search-all-media-cards';
import type { MediaDetailOptions } from 'src/sections/search-all/utils/search-all-detail-options';

import { useState, useCallback } from 'react';

import { MediaDetailDialog } from 'src/sections/search-all/components/media-detail-dialog';
import { useSearchAllShare } from 'src/sections/search-all/hooks/handler/use-search-all-share';
import { useSearchAllAdCard } from 'src/sections/search-all/hooks/load/fetch/use-search-all-ad-card';
import { useSearchAllSimilar } from 'src/sections/search-all/hooks/load/fetch/use-search-all-similar';
import { useSearchAllAdDetail } from 'src/sections/search-all/hooks/load/fetch/use-search-all-ad-detail';
import { useSearchAllVideoUrls } from 'src/sections/search-all/hooks/load/fetch/use-search-all-video-urls';
import { useSearchAllProductDetail } from 'src/sections/search-all/hooks/load/fetch/use-search-all-product-detail';

/**
 * 소재 **상세 다이얼로그 한 벌** — 카드 하나를 주면 소재 찾기와 같은 상세를 연다.
 *
 * 🔑 상세가 쥐는 것(광고 원문 · 비슷한 소재 · 상품 상세 · 영상 주소)을 여기서 다 묻는다 —
 *   부르는 쪽은 «지금 열 카드»만 준다. 그래서 어느 화면에서 열어도 같은 것이 보인다.
 * ⚠️ 비슷한 소재를 누르면 **이 안에서** 카드가 갈아 끼워진다(부르는 쪽 상태를 건드리지 않는다).
 * ⚠️ `socialOnly` 면 광고 줄을 아예 안 묻는다 — 소셜 글만 다루는 화면의 상세다.
 * 🔑 목록에서 열 때는 `nav`(이전/다음)와 목록의 영상 주소(`videoUrls`)를 함께 받는다 — 목록이 이미 받은
 *   주소로 바로 틀고, 화면이 편 순서대로 넘긴다. 비슷한 소재로 갈아 끼운 동안에는 이전/다음이 없다.
 */
export function HubMediaDetail({
  card,
  onClose,
  socialOnly = false,
  nav,
  videoUrls: listVideoUrls,
  options,
  extraSection,
}: {
  card: MediaCard | null;
  onClose: () => void;
  /** 목록의 이전/다음 — 없으면 넘기지 않는다(카드 하나만 여는 자리) */
  nav?: { hasPrev: boolean; hasNext: boolean; onPrev: () => void; onNext: () => void };
  /** 목록이 쥔 영상 주소 — 주면 그 결과를 읽고 거기에 묻는다 · 없으면 제 것을 쓴다 */
  videoUrls?: ReturnType<typeof useSearchAllVideoUrls>;
  /** 글 목록 화면이 더 싣는 것 — 검색어 형광 · 「캠페인 추가」 · 수치 시점(`MediaDetailDialog` 에 그대로) */
  options?: MediaDetailOptions;
  /** 화면이 제 글에만 더 싣는 칸 — 지금 보이는 카드(비슷한 소재로 갈아 끼운 카드 포함)로 부른다 */
  extraSection?: (card: MediaCard) => ReactNode;
  /**
   * 소셜 글만 다루는 자리인가 — 켜면 「비슷한 소재」에서 **광고를 안 묻는다**.
   * 🚨 보기만 바꾼 자리(게시글 탭의 섬네일)에 다른 원장이 섞이면 「보기를 바꿨는데
   *   내용이 달라졌다」가 된다.
   */
  socialOnly?: boolean;
}) {
  /** 비슷한 소재로 갈아 끼운 카드 — 없으면 부르는 쪽이 준 카드 */
  const [swapped, setSwapped] = useState<MediaCard | null>(null);
  const shown = swapped ?? card;

  const ownVideoUrls = useSearchAllVideoUrls();
  const videoUrls = listVideoUrls ?? ownVideoUrls;
  const adDetail = useSearchAllAdDetail(shown?.kind === 'ad' ? shown.ad?.id ?? '' : '');
  const similar = useSearchAllSimilar(shown, { ads: !socialOnly });
  const productDetail = useSearchAllProductDetail(shown);
  const swap = useCallback((next: MediaCard) => {
    setSwapped(next);
    if (next.videoAsk && !next.videoSrc) videoUrls.need(next.videoAsk);
  }, [videoUrls]);
  const similarAd = useSearchAllAdCard(swap);
  // 검색어가 없는 자리라 공유 링크에는 소재만 담긴다
  const share = useSearchAllShare('');

  const close = useCallback(() => {
    setSwapped(null);
    onClose();
  }, [onClose]);
  // 넘기면 갈아 끼운 카드를 내려놓는다 — 목록의 다음 카드가 보여야 한다
  const prev = useCallback(() => {
    setSwapped(null);
    nav?.onPrev();
  }, [nav]);
  const next = useCallback(() => {
    setSwapped(null);
    nav?.onNext();
  }, [nav]);

  return (
    <MediaDetailDialog
      card={shown}
      video={shown?.videoAsk ? videoUrls.results.get(shown.videoAsk.id) : undefined}
      hasPrev={!swapped && Boolean(nav?.hasPrev)}
      hasNext={!swapped && Boolean(nav?.hasNext)}
      onPrev={prev}
      onNext={next}
      onClose={close}
      onShare={share}
      adDoc={adDetail.doc}
      similar={similar}
      productDetail={productDetail}
      onOpenSimilarAd={similarAd.openAd}
      onOpenSimilarCard={swap}
      options={options}
      extraSection={extraSection}
    />
  );
}
